/**
 * Manifesto Reveal Module
 * ─────────────────────────────────────────────
 * Scroll-gated sequential animation.
 *
 * Each element reveals ONLY when:
 *   1. The previous element has already been revealed
 *   2. This element is currently visible in the viewport
 *      OR the user has scrolled past it
 *
 * This creates an organic "scroll-driven" sequence —
 * the animation pauses when the user stops scrolling
 * and resumes when they continue. If the user scrolls
 * past the section, the remaining elements reveal
 * automatically with their natural timing rhythm.
 *
 * Timing rhythm per element type:
 *   heading → 500ms delay (dramatic pause)
 *   line    → 220ms delay (quick beat)
 *   text    → 380ms delay (flowing)
 *   cta     → 300ms delay (final flourish)
 */

(function () {
    'use strict';

    var section = document.querySelector('.historia-manifesto');
    if (!section) return;

    var container = section.querySelector('.container');
    if (!container) return;

    var selectors = [
        '.historia-manifesto__heading',
        '.historia-manifesto__text',
        '.historia-manifesto__line',
        '.historia-cta'
    ].join(',');

    var elements = Array.from(container.querySelectorAll(selectors));
    if (!elements.length) return;

    // Track which index we should reveal next
    var nextIndex = 0;
    var isWaiting = false; // debounce: delay timer active
    var started = false;   // has the sequence started at all

    /**
     * Delay weight per element type — creates breathing rhythm.
     */
    function getDelay(el) {
        if (el.classList.contains('historia-manifesto__heading')) return 500;
        if (el.classList.contains('historia-manifesto__line')) return 220;
        if (el.classList.contains('historia-manifesto__text')) return 380;
        if (el.classList.contains('historia-cta')) return 300;
        return 350;
    }

    /**
     * Check if an element is in viewport OR has already been scrolled past.
     * Elements above the viewport (scrolled past) return true so the
     * animation keeps running even when the user scrolls below the section.
     */
    function isInViewportOrPast(el) {
        var rect = el.getBoundingClientRect();
        // Already scrolled past (above viewport) — always reveal
        if (rect.bottom <= 0) return true;
        // Currently in viewport
        return rect.top < window.innerHeight - 40;
    }

    /**
     * Try to reveal the next element in the queue.
     * Only fires if the element is visible in the viewport.
     * After revealing, schedules the next one with a type-based delay.
     */
    function tryRevealNext() {
        if (nextIndex >= elements.length) {
            // All done — clean up listeners
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            return;
        }

        if (isWaiting) return; // timer from previous element still running

        var el = elements[nextIndex];

        if (!isInViewportOrPast(el)) return; // wait for user to scroll

        // Reveal this element
        isWaiting = true;
        var delay = nextIndex === 0 ? 150 : getDelay(elements[nextIndex - 1]);

        setTimeout(function () {
            el.classList.add('is-visible');
            nextIndex++;
            isWaiting = false;

            // Immediately try the next one (it might already be in viewport)
            tryRevealNext();
        }, delay);
    }

    /**
     * Throttled scroll/resize handler
     */
    var ticking = false;
    function onScroll() {
        if (!ticking) {
            window.requestAnimationFrame(function () {
                tryRevealNext();
                ticking = false;
            });
            ticking = true;
        }
    }

    /**
     * Section-level observer — starts the whole machine
     * only when the section enters viewport.
     */
    var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (entry.isIntersecting && !started) {
                started = true;

                // Attach scroll-driven engine
                window.addEventListener('scroll', onScroll, { passive: true });
                window.addEventListener('resize', onScroll, { passive: true });

                // Kick off — first elements might already be visible
                tryRevealNext();

                sectionObserver.unobserve(section);
            }
        });
    }, {
        root: null,
        rootMargin: '0px 0px -60px 0px',
        threshold: 0.05
    });

    function init() {
        sectionObserver.observe(section);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
